import { createIdentifier } from '@blocksuite/global/di';
import { BlockSuiteError } from '@blocksuite/global/exceptions';
import { StdIdentifier } from '@blocksuite/std';
import type { Block, ExtensionType } from '@blocksuite/store';
import type { Viewport } from '../types';

export interface ViewportElementService {
  get viewportElement(): HTMLElement;
  get viewport(): Viewport;
  get viewportBlocks(): Array<Block>;
}

export const ViewportElementProvider = createIdentifier<ViewportElementService>(
  'ViewportElementProvider'
);

export const ViewportElementExtension = (selector: string): ExtensionType => {
  return {
    setup: di => {
      di.override(ViewportElementProvider, provider => {
        const getViewportElement = (): HTMLElement => {
          const std = provider.get(StdIdentifier);
          const viewportElement = std.host.closest<HTMLElement>(selector);
          if (!viewportElement) {
            throw new BlockSuiteError(
              BlockSuiteError.ErrorCode.ValueNotExists,
              `ViewportElementProvider: viewport element is not found`
            );
          }
          return viewportElement;
        };
        return {
          get viewportElement() {
            return getViewportElement();
          },
          // get the blocks in viewport, also you can get models from this function
          get viewportBlocks() {
            const std = provider.get(StdIdentifier);
            const viewportElement = getViewportElement();
            const { scrollTop, scrollLeft, clientHeight, clientWidth } =
              viewportElement;
            const viewportRect = viewportElement.getBoundingClientRect();

            // 使用 peek() 获取最新的 blocks 状态
            const blocks = Object.values(std.store.blocks.peek()).filter(
              block => {
                const blockElement = std.view.getBlock(block.id);
                if (!blockElement) return false;

                const rect = blockElement.getBoundingClientRect();
                const top = rect.top + scrollTop - viewportRect.top;
                const left = rect.left + scrollLeft - viewportRect.left;
                const bottom = top + rect.height;
                const right = left + rect.width;
                // the judgement of if the block intersects with the viewport
                const isInViewport = !(
                  bottom < scrollTop ||
                  top > scrollTop + clientHeight ||
                  right < scrollLeft ||
                  left > scrollLeft + clientWidth
                );
                const isRoot =
                  block.flavour === 'affine:note' ||
                  block.flavour === 'affine:page';
                return isInViewport && !isRoot;
              }
            );

            return blocks;
          },
          get viewport() {
            const viewportElement = getViewportElement();
            const {
              scrollLeft,
              scrollTop,
              scrollWidth,
              scrollHeight,
              clientWidth,
              clientHeight,
            } = viewportElement;
            const { top, left } = viewportElement.getBoundingClientRect();
            return {
              top,
              left,
              scrollLeft,
              scrollTop,
              scrollWidth,
              scrollHeight,
              clientWidth,
              clientHeight,
            };
          },
        };
      });
    },
  };
};
